'use client';

import { Store } from 'lucide-react';

import {
  hasSetupStoreLogoUpload,
  resolveSetupStoreLogoUrl,
} from '@/components/setup/experience/lib/setupStoreLogo';
import { cn } from '@/lib/utils';

interface SetupStoreLogoMarkProps {
  logoUrl?: string | null;
  primaryColor?: string;
  className?: string;
  imgClassName?: string;
  iconClassName?: string;
  /** sm = aside thumb · md = section title mark · lg = intro mark */
  size?: 'sm' | 'md' | 'lg';
}

const SIZE_CLASS = {
  sm: {
    root: 'w-8 h-8',
    img: '',
    icon: 'size-4',
  },
  md: {
    root: 'w-7 h-7',
    img: '',
    icon: 'size-4',
  },
  lg: {
    root: 'w-9 h-9',
    img: '',
    icon: 'size-4',
  },
} as const;

/** Merchant store identity — logo image when uploaded, otherwise branded Store icon. */
export function SetupStoreLogoMark({
  logoUrl,
  primaryColor,
  className,
  imgClassName,
  iconClassName,
  size = 'md',
}: SetupStoreLogoMarkProps) {
  const sizeClass = SIZE_CLASS[size];
  const hasLogo = hasSetupStoreLogoUpload(logoUrl);
  const src = resolveSetupStoreLogoUrl(logoUrl);

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full',
        sizeClass.root,
        className,
      )}
      style={!hasLogo && primaryColor ? { backgroundColor: primaryColor } : undefined}
      aria-hidden
    >
      {hasLogo ? (
        <img
          src={src}
          alt=""
          className={cn('bg-card h-full w-full object-contain', sizeClass.img, imgClassName)}
          draggable={false}
        />
      ) : (
        <Store className={cn('text-white', sizeClass.icon, iconClassName)} strokeWidth={2.25} />
      )}
    </span>
  );
}
